import { useEffect, useState } from 'react';
import { disputeService } from '../services/disputeService';

/**
 * Aba "Mensagens" do card de disputa — Sessão Pública do pregoeiro e diligências.
 *
 * ⛔ FONTE ÚNICA: `procurement_messages`, a MESMA tabela que o Monitorar lê,
 * via `GET /procurement/disputes/:id/messages`.
 *
 * O que havia antes: a aba lia `rankingData.mensagensSessaoPublica`, um blob que
 * SÓ a extensão alimentava. MEDIDO em 25/08/2026 — o bucket existia em 2 de 54
 * sessões, ambas de 19/05/2026. A aba ficou vazia por três meses e ninguém
 * notou, porque coleta que falha em silêncio é idêntica a "não houve mensagem".
 * No dia da medição o pregoeiro questionava o 1º colocado enquanto a tela dizia
 * "Sem mensagens coletadas ainda".
 *
 * ⭐ De quebra, o blob só enxergava do PLAY em diante. Pela tabela vem também o
 * que foi dito ANTES da disputa começar (na Dispensa 96/2026, 5 mensagens de
 * abertura às 09:00, com play só às 14:51).
 */

type Msg = {
  id: number;
  origin: string;
  messageDate: string;
  messageText: string;
  itemReference?: string | null;
  mentionsIpcom?: boolean;
  isConvocacao?: boolean;
};

/** Os dois dialetos gravados na coluna `origin`: o playwright-automation-service
 *  escreve 'participante'/'comprador', o backend escreve 'fornecedor'/'pregoeiro'.
 *  Espelha `origemCanonica()` do backend. */
function canonica(origin?: string): string {
  const o = (origin || '').toLowerCase();
  if (o === 'participante') return 'fornecedor';
  if (o === 'comprador') return 'pregoeiro';
  return o || 'sistema';
}

const ESTILO: Record<string, { rotulo: string; caixa: string; texto: string }> = {
  pregoeiro: { rotulo: '⚖ Pregoeiro', caixa: 'bg-blue-500/10 border-blue-700', texto: 'text-blue-300' },
  fornecedor: { rotulo: '🏢 Fornecedor', caixa: 'bg-central-cartao border-central-borda', texto: 'text-slate-300' },
  sistema: { rotulo: '⚙ Sistema', caixa: 'bg-central-elevado border-slate-800', texto: 'text-slate-500' },
  bll: { rotulo: '📩 BLL', caixa: 'bg-central-cartao border-central-borda', texto: 'text-slate-400' },
  bnc: { rotulo: '📩 BNC', caixa: 'bg-central-cartao border-central-borda', texto: 'text-slate-400' },
};

function fmtData(s: string): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return String(s);
  }
}

export function DisputeMessagesInline({ sessionId }: { sessionId: number }) {
  const [msgs, setMsgs] = useState<Msg[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setMsgs(null);
    setErro(null);
    disputeService
      .getMessages(sessionId)
      .then((d: Msg[]) => vivo && setMsgs(Array.isArray(d) ? d : []))
      .catch((e: any) => vivo && setErro(e?.response?.data?.message || e?.message || 'falha ao carregar'));
    return () => {
      vivo = false;
    };
  }, [sessionId]);

  if (erro) {
    return <div className="p-6 text-center text-sm text-red-400">Não foi possível carregar as mensagens: {erro}</div>;
  }
  if (msgs === null) {
    return <div className="p-6 text-center text-sm text-slate-500">Carregando mensagens…</div>;
  }
  if (msgs.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-slate-500">
        Nenhuma mensagem nesta licitação.
        <div className="mt-1 text-xs text-slate-600">
          Sessão Pública do pregoeiro e diligências, coletadas pelo Monitorar.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4">
      <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">
        {msgs.length} mensagem{msgs.length > 1 ? 's' : ''} · mesma fonte do Monitorar
      </div>
      {msgs.map((m) => {
        const est = ESTILO[canonica(m.origin)] || ESTILO.sistema;
        const destaque = m.isConvocacao || m.mentionsIpcom;
        return (
          <div
            key={m.id}
            className={`rounded-lg border p-3 text-sm ${
              destaque ? 'border-amber-600 bg-amber-500/10' : est.caixa
            }`}
          >
            <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
              <span className={`font-medium ${destaque ? 'text-amber-300' : est.texto}`}>
                {est.rotulo}
                {m.itemReference && <span className="ml-1.5 text-slate-600">· {m.itemReference}</span>}
                {m.isConvocacao && <span className="ml-1.5 text-amber-400">· convocação</span>}
                {m.mentionsIpcom && <span className="ml-1.5 text-amber-400">· cita a IPCOM</span>}
              </span>
              <span className="font-mono">{fmtData(m.messageDate)}</span>
            </div>
            <div className="whitespace-pre-wrap text-slate-200">{m.messageText}</div>
          </div>
        );
      })}
    </div>
  );
}
